/* eslint-disable complexity, max-lines */
import axios from 'axios';
import {
  resolve_workflow_display_stage
} from '@/services/onboarding_journey_service';
import { infer_journey_key_from_name } from '@/services/onboarding_case_service_helpers';
import {
  build_onboarding_journey_query,
  resolve_onboarding_journey_filter
} from '@/services/onboarding_journey_filter_service';
import {
  DASHBOARD_WORKFLOW_STATUS_COUNTS_API_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';
import {
  DEFAULT_JOURNEY_ICON_CONFIG,
  ONBOARDING_JOURNEY_ICON_BY_KEY
} from './config_sidebar_constants';

export const get_workflow_record_id = (workflow = {}) => Number(
  workflow?.id
  || workflow?.workflow_id
  || workflow?.entity_workflow_id
  || 0
);

const DISALLOWED_ROUTE_QUERY_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
function to_safe_route_query_key(key) {
  const safe_key = String(key || '').trim();
  return safe_key && !DISALLOWED_ROUTE_QUERY_KEYS.has(safe_key) ? safe_key : '';
}

function has_own_route_query_key(query, safe_key) {
  return Object.hasOwn(query, safe_key);
}

function can_read_route_query(query, safe_key) {
  return Boolean(safe_key && query && typeof query === 'object');
}

function read_route_query_value(query = {}, key = '') {
  const safe_key = to_safe_route_query_key(key);
  if (!can_read_route_query(query, safe_key)) return undefined;
  return has_own_route_query_key(query, safe_key)
    ? Reflect.get(query, safe_key)
    : undefined;
}

export function should_apply_gate_items(vm, gate_load_token, gate_items) {
  return (
    vm._sidebar_gate_load_token === gate_load_token
    && Array.isArray(gate_items)
  );
}

function normalize_route_value(value) {
  return String(value || '').trim().toLowerCase();
}

function get_active_journey_id(route = {}) {
  return normalize_route_value(resolve_onboarding_journey_filter(route).id);
}

function get_active_journey_label(route = {}) {
  const journey_filter = resolve_onboarding_journey_filter(route);
  return journey_filter.id ? normalize_route_value(journey_filter.label) : '';
}

function get_journey_match_values(journey_id = '', journey_label = '') {
  return [journey_id, journey_label].map(normalize_route_value).filter(Boolean);
}

function case_matches_journey(case_item = {}, journey_id = '', journey_label = '') {
  const normalized_journey_id = normalize_route_value(journey_id);
  const match_values = get_journey_match_values(normalized_journey_id, journey_label);
  if (!match_values.length) return true;

  const candidate_values = [
    case_item?.entity_type_id,
    case_item?.threshold_entity_type_id_used,
    case_item?.journey_tone,
    case_item?.journey_key,
    case_item?.journey
  ];
  return candidate_values.some((value) => match_values.includes(normalize_route_value(value)));
}

function entity_type_matches_journey(entity_type = {}, journey_id = '', journey_label = '') {
  const normalized_journey_id = normalize_route_value(journey_id);
  const match_values = get_journey_match_values(normalized_journey_id, journey_label);
  if (!match_values.length) return true;

  const candidate_values = [
    entity_type?.id,
    entity_type?.journey_key,
    entity_type?.label,
    entity_type?.name,
    infer_journey_key_from_name(entity_type?.source_name || entity_type?.name || '')
  ];
  return candidate_values.some((value) => match_values.includes(normalize_route_value(value)));
}

function get_case_scope_entity_type_id(scope = {}) {
  return String(scope?.entity_type_id || '').trim();
}

function case_matches_entity_type_scope(case_item = {}, scope = {}) {
  const entity_type_id = get_case_scope_entity_type_id(scope);
  if (!entity_type_id) return true;

  const candidate_values = [
    case_item?.entity_type_id,
    case_item?.threshold_entity_type_id_used
  ];
  return candidate_values.some((value) => String(value || '').trim() === entity_type_id);
}

function entity_type_matches_case_scope(entity_type = {}, scope = {}) {
  const entity_type_id = get_case_scope_entity_type_id(scope);
  if (!entity_type_id) return true;
  return String(entity_type?.id || '').trim() === entity_type_id;
}

function get_current_journey_query(route = {}) {
  return build_onboarding_journey_query(route);
}

export function get_case_status_label(case_item = {}) {
  const values = [
    case_item?.workflow_display_stage,
    case_item?.current_workflow_status,
    case_item?.stage,
    case_item?.detail,
    case_item?.activation,
    case_item?.workflow_status_name,
    case_item?.workflow_status,
    case_item?.status
  ];
  return String(values.find((value) => String(value || '').trim()) || '').trim();
}

function should_preserve_current_journey(item = {}, route_name = '') {
  return (
    item?.id !== 'all-journeys'
    && route_name !== 'DynamicOnboardingJourney'
  );
}

function get_applicant_entity_type_id(case_item = {}) {
  return Number(case_item?.entity_type_id || case_item?.threshold_entity_type_id_used || 0);
}

function get_applicant_entity_profile_id(case_item = {}) {
  return Number(
    case_item?.entity_profile_id
    || case_item?.entity_profile?.id
    || case_item?.profile?.entity_profile_id
    || case_item?.profile?.id
    || 0
  );
}

function get_applicant_application_id(case_item = {}) {
  return Number(case_item?.application_id || case_item?.id || 0);
}

function build_applicant_entity_type(case_item = {}, id = 0) {
  return {
    id,
    name: case_item?.journey || case_item?.entity_type_name || 'Onboarding'
  };
}

function merge_applicant_entity_type(type_map, case_item = {}) {
  const id = get_applicant_entity_type_id(case_item);
  if (id <= 0 || type_map.has(id)) return;
  type_map.set(id, build_applicant_entity_type(case_item, id));
}

export function unique_applicant_entity_types(cases = []) {
  const type_map = new Map();
  cases.forEach((case_item) => {
    merge_applicant_entity_type(type_map, case_item);
  });
  return Array.from(type_map.values());
}

function build_applicant_all_journeys_item(cases = []) {
  return {
    id: 'all-journeys',
    label: 'All Journeys',
    meta: 'My onboardings',
    icon: 'bi-grid',
    icon_tone: 'teal',
    route_name: 'MyQueue',
    route_query: {},
    tone: 'neutral',
    count: String(cases.length || 0)
  };
}

function build_applicant_journey_item(entity_type = {}) {
  const item = build_entity_type_item(entity_type);
  return {
    ...item,
    route_name: 'MyQueue',
    route_params: undefined,
    route_query: {
      journey: String(item.id),
      journey_label: item.label
    }
  };
}

export function build_applicant_entity_type_items(cases = []) {
  const journey_items = unique_applicant_entity_types(cases)
    .map(build_applicant_journey_item);
  return [
    build_applicant_all_journeys_item(cases),
    ...journey_items
  ];
}

function case_matches_scope(case_item = {}, scope = null) {
  if (!scope) return true;

  const scope_application_id = String(scope?.application_id || '').trim();
  const scope_entity_type_id = String(scope?.entity_type_id || '').trim();
  const case_application_id = String(get_applicant_application_id(case_item) || '').trim();
  const case_entity_type_id = String(get_applicant_entity_type_id(case_item) || '').trim();

  if (scope_application_id && case_application_id === scope_application_id) {
    return true;
  }

  if (scope_entity_type_id && case_entity_type_id === scope_entity_type_id) {
    return true;
  }

  return false;
}

function get_applicant_scoped_case(cases = [], scope = null) {
  const applicant_cases = Array.isArray(cases) ? cases : [];
  return applicant_cases.find((case_item) => case_matches_scope(case_item, scope)) || applicant_cases[0] || null;
}

function build_applicant_esigning_route_query(cases = [], scope = null) {
  const scoped_case = get_applicant_scoped_case(cases, scope);
  const entity_profile_id = get_applicant_entity_profile_id(scoped_case);
  const entity_type_id = get_applicant_entity_type_id(scoped_case);
  const application_id = get_applicant_application_id(scoped_case);
  const route_query = {
    journey_label: 'My onboardings'
  };

  if (entity_profile_id > 0) {
    route_query.entity_profile_id = String(entity_profile_id);
  }

  if (entity_type_id > 0) {
    route_query.entity_type_id = String(entity_type_id);
  }

  if (application_id > 0) {
    route_query.application_id = String(application_id);
  }

  return route_query;
}

export function applicant_gate_route(gate_item = {}, cases = [], scope = null) {
  if (gate_item?.route_name === 'OnboardingEsigningSetup') {
    return {
      ...gate_item,
      route_name: gate_item.route_name,
      route_query: build_applicant_esigning_route_query(cases, scope)
    };
  }

  if (['documents', 'verification', 'manual-verification'].includes(gate_item?.id)) {
    return {
      ...gate_item,
      route_name: gate_item.route_name,
      route_query: {}
    };
  }

  return {
    ...gate_item,
    route_name: 'MyQueue',
    route_query: {
      status: gate_item.label || gate_item.id
    }
  };
}

function merge_applicant_gate_item(gate_map, gate_item = null) {
  if (!gate_item) return;
  const existing_item = gate_map.get(gate_item.id);
  if (!existing_item) {
    gate_map.set(gate_item.id, gate_item);
    return;
  }

  gate_map.set(gate_item.id, {
    ...existing_item,
    ...gate_item,
    count: String(Math.max(Number(existing_item.count || 0), Number(gate_item.count || 0)))
  });
}

export function merge_applicant_gate_items(vm, base_items = [], configured_items = []) {
  if (Array.isArray(configured_items)) {
    return configured_items.map((item) => item).sort(vm.sort_gate_items);
  }

  const gate_map = new Map();
  base_items.forEach((item) => {
    merge_applicant_gate_item(gate_map, item);
  });
  return Array.from(gate_map.values()).sort(vm.sort_gate_items);
}

function with_resolved_workflow_stage(case_item = {}) {
  const workflow_stage = resolve_workflow_display_stage(case_item);
  if (!workflow_stage) return case_item;
  return { ...case_item, workflow_display_stage: workflow_stage };
}

export function normalize_sidebar_cases(cases = []) {
  return (Array.isArray(cases) ? cases : []).map(with_resolved_workflow_stage);
}

export function get_scoped_sidebar_data(cases = [], entity_type_items = [], route = {}, scope = null) {
  const active_journey_id = get_active_journey_id(route);
  const active_journey_label = get_active_journey_label(route);
  const scoped_cases = cases.filter((case_item) => (
    case_matches_journey(case_item, active_journey_id, active_journey_label)
    && case_matches_entity_type_scope(case_item, scope)
  ));
  const scoped_entity_type_items = entity_type_items.filter((entity_type) => (
    entity_type.id === 'all-journeys'
    || (
      entity_type_matches_journey(entity_type, active_journey_id, active_journey_label)
      && entity_type_matches_case_scope(entity_type, scope)
    )
  ));
  return { scoped_cases, scoped_entity_type_items };
}

export function get_gate_entity_type_items(entity_type_items = [], scope = null) {
  return scope
    ? entity_type_items.filter((entity_type) => entity_type_matches_case_scope(entity_type, scope))
    : entity_type_items;
}

export function build_case_scope_from_case(case_item = {}) {
  const entity_type_id = String(
    case_item?.entity_type_id
    || case_item?.threshold_entity_type_id_used
    || ''
  ).trim();
  if (!entity_type_id) return null;

  return {
    application_id: String(case_item?.application_id || ''),
    entity_profile_id: String(get_applicant_entity_profile_id(case_item) || ''),
    entity_type_id,
    entity_type_label: String(case_item?.journey || case_item?.entity_type_name || '').trim()
  };
}

export function get_sidebar_source_cases(vm, cases = []) {
  const has_latest_cases = Array.isArray(vm.latest_onboarding_sidebar_cases)
    && vm.latest_onboarding_sidebar_cases.length;
  return has_latest_cases ? vm.latest_onboarding_sidebar_cases : cases;
}

export function apply_loaded_sidebar_cases(vm, cases = []) {
  if (vm.is_exact_entity_role()) {
    vm.apply_applicant_onboarding_sidebar_cases(cases);
    return;
  }
  vm.apply_onboarding_sidebar_cases(cases);
}

function route_exists(router, route_name = '') {
  return router.hasRoute(route_name);
}

export function get_sidebar_route_name(router, item = {}) {
  return route_exists(router, item?.route_name) ? item.route_name : 'CommandCentre';
}

export function get_sidebar_journey_query(vm, item = {}, route_name = '') {
  if (vm.is_exact_entity_role() && item?.id === 'my-onboardings') {
    return {};
  }

  const should_preserve = vm.uses_onboarding_system_sidebar && should_preserve_current_journey(item, route_name);
  return should_preserve ? get_current_journey_query(vm.$route) : {};
}

export function has_query_values(route_query = {}) {
  return Object.keys(route_query).length > 0;
}

export function is_empty_current_query_value(vm, key = '') {
  const current_query_value = read_route_query_value(vm.$route.query, key);
  return current_query_value === undefined || String(current_query_value).trim() === '';
}

function query_value_text(value) {
  return value === undefined || value === null ? '' : String(value);
}

export function does_route_query_key_match(vm, route_query = {}, key = '') {
  const safe_key = to_safe_route_query_key(key);
  if (!safe_key) return true;
  if (!has_own_route_query_key(route_query, safe_key)) return true;
  const current_query_value = read_route_query_value(vm.$route.query, safe_key);
  const expected_query_value = read_route_query_value(route_query, safe_key);
  return query_value_text(current_query_value) === query_value_text(expected_query_value);
}

export function get_configured_entity_types(entity_types = []) {
  return (Array.isArray(entity_types) ? entity_types : [])
    .filter((entity_type) => Number(entity_type?.id) > 0 && !entity_type?.is_unconfigured);
}

function get_dashboard_status_items(result = {}) {
  if (result.status !== 'fulfilled') return [];
  const payload = result.value?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  return Array.isArray(payload) ? payload : [];
}

export function collect_gate_items(vm, status_groups = [], cases = []) {
  const gate_map = new Map();
  status_groups.flat().forEach((status) => {
    vm.merge_gate_items(gate_map, vm.build_gate_item(status, cases));
  });
  return Array.from(gate_map.values()).sort(vm.sort_gate_items);
}

export function get_workflow_status_group(result = {}) {
  if (result.status !== 'fulfilled') return [];
  return Array.isArray(result.value) ? result.value : [];
}

function first_present_text(values = []) {
  const value = values.find((item) => String(item || '').trim());
  return String(value || '').trim();
}

function get_entity_type_journey_key(entity_type = {}) {
  const source_name = first_present_text([entity_type?.source_name, entity_type?.name]);
  const inferred_journey_key = infer_journey_key_from_name(source_name);
  const configured_journey_key = first_present_text([entity_type?.journey_key]).toLowerCase();
  return first_present_text([inferred_journey_key, configured_journey_key, entity_type?.name]).toLowerCase();
}

function get_entity_type_label(entity_type = {}) {
  return first_present_text([entity_type?.name, 'Entity type']);
}

function get_entity_type_meta(entity_type = {}) {
  return first_present_text([entity_type?.description, entity_type?.entity_type_description, 'Journey']);
}

export function build_entity_type_item(entity_type = {}) {
  const journey_key = get_entity_type_journey_key(entity_type);
  const icon_config = ONBOARDING_JOURNEY_ICON_BY_KEY.get(journey_key) || DEFAULT_JOURNEY_ICON_CONFIG;
  const label = get_entity_type_label(entity_type);
  const item = {
    id: String(entity_type.id),
    label,
    meta: get_entity_type_meta(entity_type),
    icon: icon_config.icon,
    icon_tone: icon_config.icon_tone,
    route_name: 'DynamicOnboardingJourney',
    route_params: {
      journeyId: String(entity_type.id)
    },
    route_query: {
      journey_label: label
    },
    tone: 'neutral',
    count: '0'
  };
  return item;
}

export async function build_dashboard_gate_items(vm, configured_entity_types = [], cases = []) {
  const dashboard_count_results = await Promise.allSettled(
    configured_entity_types.map((entity_type) => (
      axios.get(
        DASHBOARD_WORKFLOW_STATUS_COUNTS_API_URL,
        get_request_config({ params: { entity_type_id: Number(entity_type.id) } })
      )
    ))
  );
  const dashboard_status_groups = dashboard_count_results.map(get_dashboard_status_items);
  const has_dashboard_response = dashboard_count_results.some((result) => result.status === 'fulfilled');
  return has_dashboard_response ? collect_gate_items(vm, dashboard_status_groups, cases) : null;
}

export function get_gate_items_or_empty(gate_items = []) {
  return gate_items.length ? gate_items : [];
}

export function resolve_hydrated_gate_items(vm, gate_items = [], cases = []) {
  if (Array.isArray(gate_items)) return gate_items;
  const fallback_items = vm.build_case_status_gate_items(cases);
  return fallback_items.length ? fallback_items : [];
}

